import { computed } from "vue";
import useSWRV from "swrv";
import { useAuthStore, useProductsStore } from "@/stores";
import { isAfter, isBefore, parseISO } from "date-fns";
import {
  isAnnualSponsorship,
  isVotingStageSponsorship,
  isMagazine,
  isMagazineDeposit,
  isMagazineAd,
  isMagazineAdDeposit,
  isMagazineArticle,
  isPlaque,
  isCeremonyTicket,
  isGeneralCeremonyTicket,
  isVIPCeremonyTicket,
  isUpgrade,
  isVoterRewards,
} from "best-of-awards-api";

import useAwards from "./use-awards";

export default function useProducts() {
  const { awardYear } = useAwards();
  const authStore = useAuthStore();
  const productsStore = useProductsStore();

  const { data: products, mutate } = useSWRV(
    () => (authStore.authenticated ? "/products" : null),
    () => productsStore.getProducts(),
    { refreshInterval: 60000 }
  );

  const currentProducts = computed(() => {
    return products.value?.filter((p) => !p.archived) ?? [];
  });

  const activeProducts = computed(() => {
    const now = new Date();
    return (
      products.value?.filter(
        (p) =>
          !p.archived &&
          p.active &&
          (p.availability === "Always" ||
            (p.datesAvailable &&
              isAfter(now, parseISO(p.datesAvailable[0])) &&
              isBefore(now, parseISO(p.datesAvailable[1]))))
      ) ?? []
    );
  });

  const annualSponsorship = computed(() => {
    const sponsorship = activeProducts.value?.find(isAnnualSponsorship);
    if (!sponsorship) {
      return;
    }
    return {
      ...sponsorship,
      options: sponsorship.options.filter(
        (o) => o.awardYear === awardYear.value
      ),
    };
  });

  const votingStageSponsorship = computed(() => {
    const sponsorship = activeProducts.value?.find(isVotingStageSponsorship);
    if (!sponsorship) {
      return;
    }
    return {
      ...sponsorship,
      options: sponsorship.options.filter(
        (o) => o.awardYear === awardYear.value
      ),
    };
  });

  const voterRewards = computed(() => {
    const sponsorship = activeProducts.value?.find(isVoterRewards);
    if (!sponsorship) {
      return;
    }
    return {
      ...sponsorship,
      options: sponsorship.options.filter(
        (o) => o.awardYear === awardYear.value
      ),
    };
  });

  const magazines = computed(() => {
    const magazine = activeProducts.value?.find(isMagazine);
    if (!magazine) {
      return;
    }
    magazine.options.sort((a, b) => b.awardYear - a.awardYear);
    return magazine;
  });

  const magazineDeposit = computed(() => {
    const deposit = activeProducts.value?.find(isMagazineDeposit);
    if (!deposit) {
      return;
    }

    return {
      ...deposit,
      options: deposit.options.filter((o) => o.awardYear === awardYear.value),
    };
  });

  const magazineAd = computed(() => {
    const ad = activeProducts.value?.find(isMagazineAd);
    if (!ad) {
      return;
    }

    return {
      ...ad,
      options: ad.options.filter((o) => o.awardYear === awardYear.value),
    };
  });

  const magazineAdDeposit = computed(() => {
    const deposit = activeProducts.value?.find(isMagazineAdDeposit);
    if (!deposit) {
      return;
    }

    return {
      ...deposit,
      options: deposit.options.filter((o) => o.awardYear === awardYear.value),
    };
  });

  const magazineArticle = computed(() => {
    const article = activeProducts.value?.find(isMagazineArticle);
    if (!article) {
      return;
    }

    return {
      ...article,
      options: article.options.filter((o) => o.awardYear === awardYear.value),
    };
  });

  const plaque = computed(() => {
    const plaque = activeProducts.value?.find(isPlaque);
    if (!plaque) {
      return;
    }
    plaque.options.sort((a, b) => (b.awardYear ?? 0) - (a.awardYear ?? 0));
    return plaque;
  });

  const ceremonyTicket = computed(() => {
    const ticket = activeProducts.value?.find(isCeremonyTicket);
    if (!ticket) {
      return;
    }
    for (const ticketOption of ticket.options) {
      if (
        (!isGeneralCeremonyTicket(ticketOption) &&
          !isVIPCeremonyTicket(ticketOption)) ||
        ticketOption.awardYear !== awardYear.value
      ) {
        continue;
      }
      const { nights = [] } = ticketOption;
      for (const night of nights) {
        if (night.originalQuantityAvailable > 0 && !night.quantityAvailable) {
          night.soldOut = true;
        }
      }
    }
    return ticket;
  });

  const upgrades = computed(() => {
    return activeProducts.value?.find(isUpgrade);
  });

  return {
    activeProducts,
    annualSponsorship,
    ceremonyTicket,
    currentProducts,
    magazines,
    magazineAd,
    magazineAdDeposit,
    magazineArticle,
    magazineDeposit,
    plaque,
    products,
    refresh: mutate,
    upgrades,
    voterRewards,
    votingStageSponsorship,
  };
}
